/**
 * Arrangement renderers. Each places the slots of one arrangement in order;
 * none sets a column ratio. Splits resolve to the three FmSplit recipes and
 * grids to the `--fm-grid-*` tokens in FmSection.css.
 */
import React from 'react';
import { FmAssetImage } from '../FmAssetImage';
import { FmCarousel } from '../FmCarousel';
import { FmText } from '../FmComposition';
import { FmSplit } from '../FmLayout';
import { FmTabs } from '../FmTabs';
import { FmHostedHeadingProvider, hostedHeadingFor } from '../fmHostedHeading';
import { FmElementList, FmElementView } from '../elements/FmElement';
import { keyedElements } from '../elements/elementKeys';
import { FmCopyElementView } from '../elements/FmTextElements';
import type { FmElement, FmImageElement } from '../elements/elements.types';
import type {
  FmBandSection,
  FmCarouselSection,
  FmFiguresSection,
  FmGridSection,
  FmProseSection,
  FmSection,
  FmSplitSectionDescriptor,
  FmTabbedSection,
} from './section.types';
import { resolveGovernedMediaIdentity } from '@/shared/config/contextualForestMedia';

type Ready<Section extends FmSection> = Extract<Section, { slots: object }>;

interface ArrangementProps<Section extends FmSection> {
  section: Ready<Section>;
  base: string;
}

const LEAD_BODY_KEYS = ['lede', 'body', 'list', 'sourceNote', 'badges'] as const;

/**
 * The editorial split: the lead copy's eyebrow and headline on the start rail,
 * its lede, body and every following element on the end rail. The validator
 * guarantees the lead is a copy with a heading and a body to sit beside.
 */
const EditorialProse: React.FC<ArrangementProps<FmProseSection>> = ({ section, base }) => {
  const [lead, ...rest] = section.slots.content;
  if (lead.element !== 'copy') {
    throw new Error(`Section "${section.id}" places its headline beside a non-copy lead.`);
  }
  const { eyebrow, heading, scale, testId, ...body } = lead;
  const hasLeadBody = LEAD_BODY_KEYS.some((key) => Boolean(body[key]));
  return (
    <div className="fm-section-prose fm-section-prose--beside" data-testid={`${base}-prose`}>
      <div className="fm-section-prose__headline">
        <FmCopyElementView element={{ element: 'copy', eyebrow, heading, scale, testId }} />
      </div>
      <div className="fm-section-prose__body">
        {hasLeadBody ? <FmCopyElementView element={body} /> : null}
        <FmElementList elements={rest} />
      </div>
    </div>
  );
};

const Prose: React.FC<ArrangementProps<FmProseSection>> = ({ section, base }) =>
  section.headline === 'beside' ? (
    <EditorialProse section={section} base={base} />
  ) : (
    <div className="fm-section-prose" data-testid={`${base}-prose`}>
      <FmElementList elements={section.slots.content} />
    </div>
  );

const Split: React.FC<ArrangementProps<FmSplitSectionDescriptor>> = ({ section, base }) => (
  <FmSplit
    content={
      <div className="fm-section-column">
        <FmElementList elements={section.slots.content} />
      </div>
    }
    graphic={
      <div className="fm-section-column">
        <FmElementList elements={section.slots.media} />
      </div>
    }
    side={section.side ?? 'end'}
    balance={section.balance ?? 'balanced'}
    stackOrder={section.stackOrder ?? 'content-first'}
    sticky={section.sticky ?? false}
    contentSurface="plain"
    data-testid={`${base}-split`}
  />
);

const Figures: React.FC<ArrangementProps<FmFiguresSection>> = ({ section, base }) => (
  <div className="fm-section-figures" data-testid={`${base}-figures`}>
    {section.slots.lead ? <FmElementList elements={section.slots.lead} /> : null}
    <FmElementList elements={section.slots.figures} />
    {section.slots.support ? (
      <div className="fm-section-figures__support">
        <FmElementList elements={section.slots.support} />
      </div>
    ) : null}
  </div>
);

const Grid: React.FC<ArrangementProps<FmGridSection>> = ({ section, base }) => (
  <div className="fm-section-column">
    {section.slots.lead ? <FmElementList elements={section.slots.lead} /> : null}
    <div
      className={`fm-section-grid fm-section-grid--${section.columns}`}
      data-testid={`${base}-grid`}
    >
      {keyedElements(section.slots.items).map(({ key, element }) => (
        <div key={key} className="fm-section-grid__cell">
          <FmElementView element={element} cropRole="card" />
        </div>
      ))}
    </div>
  </div>
);

const Tabbed: React.FC<ArrangementProps<FmTabbedSection>> = ({ section, base }) => (
  <div className="fm-section-column">
    {section.slots.lead ? <FmElementList elements={section.slots.lead} /> : null}
    <FmTabs
      label={section.label}
      items={section.panels.map((panel) => ({
        value: panel.key,
        label: panel.label,
        // The tab already names the panel, so an element repeating that name
        // as its own title drops it.
        content: (
          <FmHostedHeadingProvider value={hostedHeadingFor(panel.label, 4)}>
            <div className="fm-section-column">
              <FmElementList elements={panel.elements} />
            </div>
          </FmHostedHeadingProvider>
        ),
        'data-testid': panel.testId ?? `${base}-${panel.key}-tab`,
      }))}
      data-testid={`${base}-tabs`}
    />
  </div>
);

type CarouselSlide = FmCarouselSection extends infer Section
  ? Section extends { slides: readonly (infer Slide)[] }
    ? Slide
    : never
  : never;

function isImage(element: FmElement): element is FmImageElement {
  return element.element === 'image';
}

/**
 * A slide with an image reads as one immersive frame: the image fills a fixed
 * frame behind the band-tone scrim and the slide's figures and copy sit over
 * it, so every slide keeps the same height and its controls stay in view.
 * A slide without an image stacks its elements at their own height.
 */
const Slide: React.FC<{ slide: CarouselSlide; testId: string }> = ({ slide, testId }) => {
  const image = slide.elements.find(isImage);
  // A slide label a key figure already names, or a chart title opens with, is
  // not printed twice: the eyebrow over a chart titled with the same name sat
  // a section gap above the chart it named.
  const namedByElement = slide.elements.some(
    (element) =>
      (element.element === 'keyFigures' &&
        element.figures.some((figure) => figure.label === slide.label)) ||
      (element.element === 'chart' &&
        typeof element.title === 'string' &&
        element.title.startsWith(slide.label)),
  );
  const label = namedByElement ? null : (
    <FmText size="fine" tone="accent" eyebrow>
      {slide.label}
    </FmText>
  );
  if (!image) {
    return (
      <div className="fm-section-column">
        {label}
        <FmElementList elements={slide.elements} cropRole="card" />
      </div>
    );
  }
  const identity = resolveGovernedMediaIdentity({
    assetKey: image.assetKey,
    mediaContext: image.mediaContext,
  });
  const copy = slide.elements.filter((element) => element !== image);
  return (
    <div className="fm-section-slide" data-media={identity ? 'true' : 'false'}>
      {identity ? (
        <div className="fm-section-slide__media">
          <FmAssetImage
            assetKey={identity}
            altOverride={image.altOverride ?? ''}
            cropRole="background"
            data-testid={`${testId}-media`}
          />
        </div>
      ) : null}
      <div className="fm-section-column fm-section-slide__copy">
        {label}
        <FmElementList elements={copy} />
      </div>
    </div>
  );
};

const Carousel: React.FC<ArrangementProps<FmCarouselSection>> = ({ section, base }) => (
  <div className="fm-section-column">
    {section.slots.lead ? <FmElementList elements={section.slots.lead} /> : null}
    <FmCarousel
      labels={section.labels}
      slides={section.slides.map((slide) => {
        const testId = slide.testId ?? `${base}-${slide.key}-slide`;
        return {
          id: slide.key,
          content: <Slide slide={slide} testId={testId} />,
          'data-testid': testId,
        };
      })}
      data-testid={`${base}-carousel`}
    />
  </div>
);

/**
 * The background image spans the whole band behind a tone scrim; it is
 * decoration, never content. An illustration (`contain`) keeps its alt text:
 * it is placed whole beside the copy, so it is read, not just seen. The cover
 * layer stays in the accessibility tree because a registered loop carries its
 * own pause control.
 */
const BandBackground: React.FC<{
  image: FmImageElement;
  testId: string;
  fit: 'cover' | 'contain';
}> = ({ image, testId, fit }) => {
  const identity = resolveGovernedMediaIdentity({
    assetKey: image.assetKey,
    mediaContext: image.mediaContext,
  });
  if (!identity) return null;
  if (fit === 'contain') {
    return (
      <div className="fm-section-cover__illustration">
        <FmAssetImage
          assetKey={identity}
          altOverride={image.altOverride}
          data-testid={testId}
        />
      </div>
    );
  }
  return (
    <div className="fm-section-cover__media">
      <FmAssetImage
        assetKey={identity}
        altOverride={image.altOverride ?? ''}
        cropRole="background"
        data-testid={testId}
      />
    </div>
  );
};

const Band: React.FC<ArrangementProps<FmBandSection>> = ({ section, base }) => {
  const background = section.slots.background?.[0];
  const fit = section.imageFit ?? 'cover';
  return (
    <div
      className="fm-section-cover"
      data-background={background ? fit : undefined}
      data-scrim={background && fit === 'cover' ? (section.scrim ?? 'even') : undefined}
      data-testid={`${base}-band`}
    >
      {background ? (
        <BandBackground image={background} testId={`${base}-background`} fit={fit} />
      ) : null}
      <div className="fm-section-column fm-section-cover__copy">
        <FmElementList elements={section.slots.content} />
        {section.slots.actions ? <FmElementList elements={section.slots.actions} /> : null}
      </div>
    </div>
  );
};

/** Dispatches a ready, partial or stale section to its arrangement renderer. */
export const FmSectionArrangement: React.FC<{ section: FmSection; base: string }> = ({
  section,
  base,
}) => {
  if (!('slots' in section) || !section.slots) {
    throw new Error(
      `Section "${section.id}" in state "${section.state.status}" has no content to arrange.`,
    );
  }
  switch (section.arrangement) {
    case 'prose':
      return <Prose section={section} base={base} />;
    case 'split':
      return <Split section={section} base={base} />;
    case 'figures':
      return <Figures section={section} base={base} />;
    case 'grid':
      return <Grid section={section} base={base} />;
    case 'tabbed':
      return <Tabbed section={section} base={base} />;
    case 'carousel':
      return <Carousel section={section} base={base} />;
    case 'band':
      return <Band section={section} base={base} />;
  }
};
